package dev.lingvo.shared.screenshots.scene

import dev.lingvo.shared.screenshots.doc.BackgroundLayer
import dev.lingvo.shared.screenshots.doc.CanvasSize
import dev.lingvo.shared.screenshots.doc.DeviceLayer
import dev.lingvo.shared.screenshots.doc.GroupLayer
import dev.lingvo.shared.screenshots.doc.ImageLayer
import dev.lingvo.shared.screenshots.doc.ImageSource
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.shared.screenshots.doc.LayerPatch
import dev.lingvo.shared.screenshots.doc.LocaleOverride
import dev.lingvo.shared.screenshots.doc.Page
import dev.lingvo.shared.screenshots.doc.TextContent
import dev.lingvo.shared.screenshots.doc.TextLayer

internal fun joinPanorama(pages: List<Page>, canvas: CanvasSize): Page {
    require(pages.isNotEmpty() && pages.sumOf { it.slotIds.size } <= 10 && canvas.width > 0 && canvas.height > 0)
    val backgrounds = pages.map { it.layers.firstOrNull() as? BackgroundLayer }
    require(backgrounds.all { it != null }) { "Every page needs a shared background" }
    val first = requireNotNull(backgrounds.first())
    require(backgrounds.all { it?.fill == first.fill && it.blobs == first.blobs && it.grain == first.grain }) { "Panorama backgrounds must match" }
    val backgroundPatches = pages.flatMap { it.overrides.keys }.distinct().associateWith { locale ->
        val patches = pages.mapIndexed { index, page ->
            page.overrides[locale]?.patches?.get(requireNotNull(backgrounds[index]).base.id)
        }
        require(patches.distinct().size == 1) { "Panorama background locale patches must match" }
        patches.first()
    }
    val layers = mutableListOf<Layer>()
    val overrides = mutableMapOf<String, LocaleOverride>()
    val total = pages.sumOf { it.slotIds.size }
    layers += first.copy(base = first.base.copy(box = first.base.box.copy(x = 0.0, w = total * canvas.width.toDouble())))
    var offset = 0
    for ((index, page) in pages.withIndex()) {
        val dx = offset * canvas.width.toDouble()
        layers += page.layers.drop(1).map { shiftLayer(it, dx, offset) }
        val topLevel = page.layers.map { it.base.id }.toSet()
        for ((locale, override) in page.overrides) {
            val prior = overrides[locale]
            require(prior == null || prior.mirror == override.mirror) { "Panorama mirror overrides must agree" }
            require(prior == null || prior.inheritLanguage == override.inheritLanguage) { "Panorama locale inheritance must agree" }
            val backgroundId = requireNotNull(backgrounds[index]).base.id
            overrides[locale] = LocaleOverride(override.mirror, prior?.patches.orEmpty() + override.patches.filterKeys { it != backgroundId }.mapValues { (id, patch) ->
                shiftPatch(patch, if (id in topLevel) dx else 0.0, offset)
            }, override.inheritLanguage)
        }
        offset += page.slotIds.size
    }
    for ((locale, patch) in backgroundPatches) if (patch != null) {
        val override = overrides.getValue(locale)
        overrides[locale] = override.copy(patches = override.patches + (first.base.id to patch))
    }
    return Page(pages.first().id, pages.flatMap { it.slotIds }, pages.first().scene, layers, overrides)
}

internal fun separatePanorama(page: Page, canvas: CanvasSize): List<Page> {
    require(canvas.width > 0 && canvas.height > 0 && page.slotIds.isNotEmpty())
    val w = canvas.width.toDouble()
    val background = page.layers.firstOrNull() as? BackgroundLayer
    return page.slotIds.mapIndexed { k, slot ->
        val members = page.layers.filterNot { it is BackgroundLayer }.filter {
            ((it.base.box.x + it.base.box.w / 2) / w).toInt().coerceIn(0, page.slotIds.lastIndex) == k
        }
        val reservedIds = mutableSetOf<String>()
        fun collect(layer: Layer) { reservedIds += layer.base.id; if (layer is GroupLayer) layer.children.forEach(::collect) }
        members.forEach(::collect)
        val splitBackground = background?.let {
            val stem = splitId(it.base.id, k)
            var id = stem
            var suffix = 0
            while (id in reservedIds) {
                suffix++
                id = "b-${namespaceDigest("$stem:$suffix")}"
            }
            it.copy(base = it.base.copy(id = id, box = it.base.box.copy(w = w)))
        }
        val layers = listOfNotNull(splitBackground) +
            members.map { shiftLayer(it, -k * w, -k, splitting = true, planSlots = page.slotIds.size) }
        val ids = reservedIds + listOfNotNull(splitBackground?.base?.id)
        val topLevel = layers.map { it.base.id }.toSet()
        val overrides = page.overrides.mapValues { (_, override) ->
            val patches = override.patches.filterKeys { it in ids }.mapValues { (id, patch) ->
                shiftPatch(patch, if (id in topLevel) -k * w else 0.0, -k, splitting = true)
            }
            val backgroundPatch = background?.let { override.patches[it.base.id] }
            override.copy(patches = if (backgroundPatch == null) patches else
                patches + (layers.first().base.id to backgroundPatch))
        }
        Page(splitId(page.id, k), listOf(slot), page.scene, layers, overrides)
    }
}

/** A split cannot keep a reference to a caption on another page; refusal preserves the original strip. */
private fun shiftPatch(patch: LayerPatch, dx: Double, slotOffset: Int, splitting: Boolean = false): LayerPatch {
    val content = (patch.content as? TextContent.SlotCaption)?.let {
        val slot = it.slot + slotOffset
        require(!splitting || slot == 0) { "Panorama caption binding reaches another slot" }
        it.copy(slot = slot)
    } ?: patch.content
    return patch.copy(box = patch.box?.let { it.copy(x = it.x + dx) }, content = content)
}

private fun shiftLayer(layer: Layer, dx: Double, slotOffset: Int, splitting: Boolean = false, planSlots: Int = 0): Layer {
    val base = layer.base.copy(box = layer.base.box.copy(x = layer.base.box.x + dx), roleSlot = if (splitting) 0 else layer.base.roleSlot + slotOffset)
    fun source(source: ImageSource): ImageSource = when {
        source !is ImageSource.Capture -> source
        splitting -> {
            val offset = source.planOffset + source.slot + slotOffset
            // Panorama slots are in full plan order; only canonicalise offsets that exceed v1's +/-9 bound.
            source.copy(slot = 0, planOffset = if (offset in -9..9) offset else offset % planSlots)
        }
        else -> source.copy(slot = source.slot + slotOffset)
    }
    return when (layer) {
        is DeviceLayer -> layer.copy(base = base, screen = source(layer.screen))
        is ImageLayer -> layer.copy(base = base, source = source(layer.source))
        is TextLayer -> layer.copy(base = base, content = (layer.content as? TextContent.SlotCaption)?.let { it.copy(slot = it.slot + slotOffset) } ?: layer.content)
        is GroupLayer -> layer.copy(base = base, children = layer.children.map { shiftLayer(it, 0.0, slotOffset, splitting, planSlots) })
        else -> layer.rebase(base)
    }
}

private fun splitId(id: String, slot: Int): String {
    val namespace = if (id.length <= 38) id else namespaceDigest(id)
    return "$namespace-$slot"
}
